import { useCallback } from "react";
import { useAuth } from "./useAuth";
import type { PageKey } from "../types/pages";

const ROUTE_PRIORITY: Array<{ page: PageKey; href: string }> = [
  { page: "employee:dashboard",        href: "/employee/dashboard" },
  { page: "employee:payslip",          href: "/employee/payslip" },
  { page: "employee:loans",            href: "/employee/loans" },
  { page: "employee:leave",            href: "/employee/leave" },
  { page: "employee:coe",              href: "/employee/coe" },
  { page: "employee:tasks",            href: "/employee/tasks" },
  { page: "employee:wallet",           href: "/employee/wallet" },
  { page: "employee:cash-advance",     href: "/employee/cash-advance" },
  { page: "employee:work-adjustments", href: "/employee/work-adjustments" },
  { page: "admin:dashboard",           href: "/admin/dashboard" },
  { page: "admin:users",               href: "/admin/users" },
  { page: "admin:payslip",             href: "/admin/payslip" },
  { page: "admin:loans",               href: "/admin/loans" },
  { page: "admin:leave",               href: "/admin/leave" },
  { page: "admin:tasks",               href: "/admin/tasks" },
  { page: "admin:attendance",          href: "/admin/attendance" },
  { page: "admin:wallet",              href: "/admin/wallet" },
  { page: "admin:cash-advance",        href: "/admin/cash-advance" },
  { page: "admin:adjustments",         href: "/admin/adjustments" },
  { page: "admin:work-adjustments",    href: "/admin/work-adjustments" },
  { page: "admin:reports",             href: "/admin/reports" },
  { page: "admin:ledger",              href: "/admin/ledger" },
  { page: "store:manage-store",        href: "/store" },
];

/**
 * Pure resolver: the best starting route for a given page set.
 * Super-admin ("*") always lands on the admin dashboard.
 */
export function routeForPages(pages: PageKey[] | "*"): string {
  if (pages === "*") return "/admin/dashboard";
  return ROUTE_PRIORITY.find((p) => pages.includes(p.page))?.href ?? "/unauthorized";
}

/**
 * Returns the best starting route for the current user based on their
 * permitted pages. Super-admin always lands on the admin dashboard.
 * Returns "/" while auth is still loading.
 */
export function useDefaultRoute(): string {
  const { isAdmin, hasPageAccess, user, loading } = useAuth();

  if (loading || !user) return "/";
  if (isAdmin) return "/admin/dashboard";

  return ROUTE_PRIORITY.find((p) => hasPageAccess(p.page))?.href ?? "/unauthorized";
}

export interface LandingTarget {
  businessId: string | null;
  route: string;
}

/**
 * Resolves a landing target the user can actually reach, scanning ALL their
 * businesses (active one first) rather than only the currently-active business.
 * Callers should switch to `businessId` before navigating to `route` so the
 * per-business route guard passes. Prevents dead-ends on /unauthorized.
 */
export function useLandingTarget(): () => LandingTarget {
  const { isAdmin, businesses, activeBusinessId, pagesForBusiness } = useAuth();

  return useCallback((): LandingTarget => {
    if (isAdmin) return { businessId: activeBusinessId, route: "/admin/dashboard" };

    const ordered = [
      ...businesses.filter((b) => b.businessId === activeBusinessId),
      ...businesses.filter((b) => b.businessId !== activeBusinessId),
    ];
    for (const b of ordered) {
      const route = routeForPages(pagesForBusiness(b.businessId));
      if (route !== "/unauthorized") return { businessId: b.businessId, route };
    }
    return { businessId: activeBusinessId, route: "/" };
  }, [isAdmin, businesses, activeBusinessId, pagesForBusiness]);
}
